import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useUrlParams } from '../params'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, type Host, type NodeInfo } from '../../api'
import { HostCard } from '../Hosts'
import { NODES_KEY, NodeManage, outputsText, useProvisioning } from '../Nodes'
import { FactorBadge, SecondFactorProvider } from '../SecondFactor'
import { HostsLiveContext } from '../provisioning'
import { NODES_PATH, nodePath, relayKeys, type NodeTab, type RelayRoute } from './api'
import { RelayDataBanner } from './banner'
import { clock, dur } from './format'
import { useRelayLive, type RelayLive } from './live'
import { RoleGlyphs } from './nodeParts'
import { useNode, useNodeLinks, useNodeRoutes, withLive } from './nodes'
import { NodeOverview } from './NodeOverview'
import { NodeEvents, NodeStreams, useNames } from './NodeTabs'
import { LogicVersion, NodeUpdate } from './NodeUpdate'
import { RevokeNode } from './RevokeNode'
import { NodeLinks } from './NodeLinks'
import { RelayKeyBlock } from './RelayKey'
import { AGENT_SPEC, NODE_SPEC, nodeIssues, nodeView } from './states'
import { Copy, Empty, ErrorBox, NetPanel, SkeletonRows, StateBadge, StateText } from './ui'

/*
 * The node card (handoff/nodes.md, design #node/<id>?tab=): /admin/network/nodes/:id.
 * Header: name, state (glyph + word + the reasons), roles as shapes, region and
 * provider, version, agent, egress mode; an offline banner with the last report.
 * Tabs (?tab=): Overview (load now, why degraded, roles and relay, history),
 * Streams (routes through the node), Links (with the link panel, ?link=),
 * Events (relay journal of the node), Relay key, Host & agent (M2.4 host card
 * and the node's policy, secret and revoke). Update logic and Update holders
 * sit in the header; the node table is unchanged apart from the version line.
 */

const TABS: { id: NodeTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'streams', label: 'Streams' },
  { id: 'links', label: 'Links' },
  { id: 'events', label: 'Events' },
  { id: 'key', label: 'Relay key' },
  { id: 'host', label: 'Host & agent' },
]

export function NodeCardPage() {
  return (
    <SecondFactorProvider>
      <NodeCard />
    </SecondFactorProvider>
  )
}

function NodeCard() {
  const { id = '' } = useParams()
  const live = useRelayLive()
  const prov = useProvisioning()
  const q = useNode(id, live)
  const crumbs = (name?: string) => (
    <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-2 text-sm">
      <Link to={NODES_PATH} className="text-muted hover:text-text">
        Nodes
      </Link>
      <span className="text-faint" aria-hidden>
        /
      </span>
      <span className="font-mono text-text-soft">{name ?? id}</span>
    </nav>
  )
  let body: ReactNode
  if (q.isPending) {
    body = (
      <div className="flex flex-col gap-4" aria-busy="true" data-testid="node-card-loading">
        <div className="h-[92px] animate-breathe rounded-lg border border-line bg-panel" />
        <NetPanel>
          <SkeletonRows rows={5} cols={6} />
        </NetPanel>
      </div>
    )
  } else if (q.error && !q.data) {
    // a failed refetch keeps the last answer on screen (the banner says how old it is)
    body =
      q.error instanceof ApiError && q.error.status === 404 ? (
        <Empty
          title={`No node “${id}”`}
          testId="node-not-found"
          action={
            <Link to={NODES_PATH} className="inline-flex min-h-9 items-center rounded-md border border-line-2 px-3 text-sm font-medium hover:border-slot max-md:min-h-[44px]">
              All nodes
            </Link>
          }
        >
          It may have been deleted, or the link is mistyped. Revoked nodes stay in the list under “Show revoked”.
        </Empty>
      ) : (
        <ErrorBox testId="node-card-error" text={`GET /api/admin/nodes/${id}: ${q.error instanceof ApiError ? `${q.error.status} ${q.error.message}` : (q.error as Error).message}`} busy={q.isFetching} onRetry={() => void q.refetch()} />
      )
  } else {
    const n = withLive(q.data!.data, live.nodes.get(id), q.data!.data.as_of)
    const host = prov.on ? ((prov.hosts.data ?? []).find((h) => h.node_id === n.id && !h.revoked_at) ?? null) : undefined
    body = <Card n={n} live={live} host={host} hostsOn={prov.on} />
  }
  const name = q.data?.data.name
  useEffect(() => {
    document.title = name ? `${name} · Nodes` : 'Node'
  }, [name])
  return (
    <HostsLiveContext.Provider value={prov.live}>
      <div data-testid="node-card-page">
        {crumbs(name)}
        {live.available !== false && <RelayDataBanner live={live} />}
        {body}
      </div>
    </HostsLiveContext.Provider>
  )
}

function Card({ n, live, host, hostsOn }: { n: NodeInfo; live: RelayLive; host: Host | null | undefined; hostsOn: boolean }) {
  const { params, update } = useUrlParams()
  const tab = (TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'overview') as NodeTab
  const relayOn = live.available === true && !!n.relay
  const routes = useNodeRoutes(n.id, live)
  const links = useNodeLinks(n.id, live)
  const tabs = TABS.filter((t) => relayOn || t.id === 'overview' || t.id === 'host')
  const shown = tabs.some((t) => t.id === tab) ? tab : 'overview'
  const count: Partial<Record<NodeTab, number>> = relayOn ? { streams: routes.items.length + (n.relay?.no_path?.length ?? 0), links: links.items.length } : {}
  const tabRefs = useRef<Record<string, HTMLAnchorElement | null>>({})
  const nav = useNavigate()
  const go = (t: NodeTab) => {
    // a tab is a step in the history (Back returns to the previous tab)
    update({ tab: t === 'overview' ? null : t, link: null }, { replace: false })
  }
  // arrow keys between tabs (handoff §6), Enter / click follow the link
  const onKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!
    tabRefs.current[next.id]?.focus()
    void nav(nodePath(n.id, next.id))
  }
  return (
    <div className="flex flex-col gap-4" data-testid="node-card" data-node={n.id} data-view={nodeView(n)}>
      <Header n={n} host={host} hostsOn={hostsOn} relayOn={relayOn} />
      <nav aria-label="Node sections" className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <ul className="flex min-w-max gap-1 border-b border-line" role="list">
          {tabs.map((t, i) => (
            <li key={t.id}>
              <Link
                ref={(el) => {
                  tabRefs.current[t.id] = el
                }}
                to={nodePath(n.id, t.id)}
                aria-current={shown === t.id ? 'page' : undefined}
                data-testid={`node-tab-${t.id}`}
                onKeyDown={(e) => onKey(e, i)}
                onClick={(e) => {
                  e.preventDefault()
                  go(t.id)
                }}
                className={`-mb-px inline-flex min-h-10 items-center gap-1.5 border-b-2 px-3 text-sm font-medium whitespace-nowrap max-md:min-h-[44px] ${shown === t.id ? 'border-live text-text' : 'border-transparent text-muted hover:text-text-soft'}`}
              >
                {t.label}
                {count[t.id] !== undefined && <span className="num font-mono text-xs text-muted">{count[t.id]}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div data-testid={`node-panel-${shown}`}>
        {shown === 'overview' && <NodeOverview n={n} live={live} routes={routes.items} relayOn={relayOn} />}
        {shown === 'streams' && <NodeStreams n={n} live={live} routes={routes} />}
        {shown === 'links' && <NodeLinks n={n} live={live} links={links} />}
        {shown === 'events' && <NodeEvents n={n} live={live} />}
        {shown === 'key' && n.relay && <KeyTab n={n} routes={routes.items} />}
        {shown === 'host' && <HostTab n={n} host={host} hostsOn={hostsOn} />}
      </div>
    </div>
  )
}

function Header({ n, host, hostsOn, relayOn }: { n: NodeInfo; host: Host | null | undefined; hostsOn: boolean; relayOn: boolean }) {
  const v = nodeView(n)
  const issues = nodeIssues(n)
  return (
    <section className="rs-corners rounded-lg border border-line px-4 py-4" aria-label={`Node ${n.name}`} data-testid="node-header">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="font-mono text-2xl leading-tight font-semibold break-all" data-testid="node-name">
              {n.name}
            </h1>
            <Copy value={n.id} label={`node id ${n.id}`} />
            <StateBadge spec={NODE_SPEC[v]} testId="node-state" />
          </div>
          {issues.length > 0 && (
            <p className="text-sm text-warn" data-testid="node-header-issues">
              ▲ {issues.join(' · ')}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-text-soft">
            {relayOn && n.roles && (
              <>
                <RoleGlyphs roles={n.roles} labels testId="node-header-roles" />
                <span className="text-faint" aria-hidden>
                  ·
                </span>
              </>
            )}
            <span>
              <span className="font-mono text-xs">{n.region || '—'}</span>
              {n.provider ? ` · ${n.provider}` : ''}
            </span>
            <span className="text-faint" aria-hidden>
              ·
            </span>
            <span className="num text-xs">
              <LogicVersion n={n} host={host} empty="version —" />
            </span>
            <span className="text-faint" aria-hidden>
              ·
            </span>
            <span className="text-xs text-muted">egress {n.egress === 'via_center' ? 'via center' : 'direct'}</span>
            {hostsOn && (
              <>
                <span className="text-faint" aria-hidden>
                  ·
                </span>
                {host ? <StateText spec={host.online ? AGENT_SPEC.on : AGENT_SPEC.off} testId="node-header-agent" /> : <StateText spec={AGENT_SPEC.none} testId="node-header-agent" />}
              </>
            )}
            {!n.accept_ingest && n.status !== 'revoked' && <span className="text-xs text-warn">out of rotation: no new streams, running ones continue</span>}
          </div>
        </div>
        <Link to={nodePath(n.id, 'host')} className="inline-flex min-h-9 items-center self-start rounded-md border border-line-2 px-3 text-sm font-medium text-text hover:border-slot max-md:min-h-[44px] lg:self-end">
          Host & settings
        </Link>
      </div>
      <NodeUpdate n={n} host={host} hostsOn={hostsOn} />
      {n.status === 'offline' && (
        <div role="alert" data-testid="node-offline" className="mt-4 flex flex-col gap-1 rounded-md border border-fail/50 bg-fail/[.06] p-3 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <span className="font-mono text-[11px] font-semibold tracking-[.08em] text-fail">✕ NO LINK</span>
          <span className="text-text-soft">
            Last report <b className="num text-text">{clock(n.last_seen_at)}</b> ({dur(n.last_seen_at)} ago). Its relay legs are down; routes through it have no path until it
            returns or the controller picks another hub.
            {host && !host.online && ' The host agent is offline too: the server may be down.'}
          </span>
        </div>
      )}
      {n.status === 'revoked' && (
        <p className="mt-4 rounded-md border border-line-2 px-3 py-2 text-sm text-text-soft" data-testid="node-revoked">
          Revoked {clock(n.revoked_at)}: removed from the peers list; it can’t rejoin with this key. Kept here for the history.
        </p>
      )}
    </section>
  )
}

/** Relay key: the key block and the revoke (handoff/nodes.md KeyTab; decision 48). */
function KeyTab({ n, routes }: { n: NodeInfo; routes: RelayRoute[] }) {
  const qc = useQueryClient()
  const names = useNames()
  const changed = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: relayKeys.node(n.id) }), qc.invalidateQueries({ queryKey: NODES_KEY })])
  }
  return (
    <div className="flex flex-col gap-4">
      <RelayKeyBlock n={n} nameOf={names.nameOf} />
      {n.status !== 'revoked' && (
        <NetPanel testId="node-key-revoke">
          <RevokeNode n={n} routes={routes} onRevoked={changed} />
        </NetPanel>
      )}
    </div>
  )
}

function HostTab({ n, host, hostsOn }: { n: NodeInfo; host: Host | null | undefined; hostsOn: boolean }) {
  const qc = useQueryClient()
  const prov = useProvisioning()
  const changed = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: relayKeys.node(n.id) }), qc.invalidateQueries({ queryKey: NODES_KEY })])
  }
  return (
    <div className="flex flex-col gap-4">
      <NetPanel title="Host agent" aside={hostsOn ? <FactorBadge /> : undefined} testId="node-host">
        {!hostsOn ? (
          <p className="text-sm text-muted">Automatic installs are not set up on this center (no provisioner): no host agent, no host commands.</p>
        ) : host ? (
          <HostCard hostId={host.id} info={prov.keys.data?.install} node={n} />
        ) : (
          <p className="text-sm text-muted" data-testid="node-host-manual">
            Installed by hand with a join token: there is no host agent on this server, so no host commands. Reinstall it over SSH to manage the host from here.
          </p>
        )}
      </NetPanel>
      {n.status === 'revoked' ? (
        <p className="text-sm text-muted">Revoked: its settings can’t change any more.</p>
      ) : (
        <NetPanel title="Node settings" testId="node-settings">
          <p className="mb-4 text-xs text-muted">
            Streams {n.streams} · outputs {outputsText(n.outputs)}. Policy, secret and revoke are the same as in the node list’s “Manage”.
          </p>
          <NodeManage n={n} onChanged={changed} onClose={() => undefined} />
        </NetPanel>
      )}
    </div>
  )
}
